import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../App";
import Layout from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Search,
  FolderOpen,
  FileText,
  Download,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Trash2,
  HardDrive,
  RefreshCw,
  Clock,
  Play,
  Power,
  Plus,
} from "lucide-react";
import { toast } from "sonner";

function ScanResultsTable({ results, onImportFiles, scanId, scanStatus }) {
  const [selectedFiles, setSelectedFiles] = useState(new Set());

  const toggleFile = (filePath) => {
    setSelectedFiles((prev) => {
      const next = new Set(prev);
      if (next.has(filePath)) next.delete(filePath);
      else next.add(filePath);
      return next;
    });
  };

  const toggleAll = () => {
    const importable = results.filter((r) => !r.imported);
    if (selectedFiles.size === importable.length) {
      setSelectedFiles(new Set());
    } else {
      setSelectedFiles(new Set(importable.map((r) => r.file_path)));
    }
  };

  const importable = results.filter((r) => !r.imported);
  const canImport = selectedFiles.size > 0 && scanStatus === "completed";

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const typeColors = {
    mechanical: "#2563EB",
    electrical: "#EAB308",
    hydraulic: "#10B981",
    facilities_electrical: "#8B5CF6",
    facilities_structural: "#F97316",
  };

  const typeLabels = {
    mechanical: "Mechanical",
    electrical: "Electrical",
    hydraulic: "Hydraulic",
    facilities_electrical: "Fac. Electrical",
    facilities_structural: "Fac. Structural",
  };

  return (
    <div>
      {importable.length > 0 && (
        <div className="flex items-center justify-between mb-4">
          <label className="flex items-center gap-2 text-sm text-[#64748B] cursor-pointer">
            <input
              type="checkbox"
              checked={selectedFiles.size === importable.length && importable.length > 0}
              onChange={toggleAll}
              className="rounded border-[#CBD5E1]"
              data-testid="select-all-files"
            />
            Select all importable ({importable.length})
          </label>
          {canImport && (
            <Button
              onClick={() => onImportFiles(scanId, Array.from(selectedFiles))}
              className="bg-[#10B981] hover:bg-[#059669] text-white rounded-none text-sm"
              data-testid="import-selected-button"
            >
              <Download size={14} className="mr-2" />
              Import {selectedFiles.size} file(s)
            </Button>
          )}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="scan-results-table">
          <thead>
            <tr className="border-b border-[#E2E8F0] text-left">
              <th className="pb-2 pr-3 w-8"></th>
              <th className="pb-2 pr-3 text-[#64748B] font-medium">File</th>
              <th className="pb-2 pr-3 text-[#64748B] font-medium">Type</th>
              <th className="pb-2 pr-3 text-[#64748B] font-medium">Machine #</th>
              <th className="pb-2 pr-3 text-[#64748B] font-medium">Size</th>
              <th className="pb-2 text-[#64748B] font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {results.map((file, idx) => (
              <tr
                key={idx}
                className="border-b border-[#F1F5F9] hover:bg-[#F8FAFC] transition-colors"
                data-testid={`scan-result-row-${idx}`}
              >
                <td className="py-2 pr-3">
                  {!file.imported && (
                    <input
                      type="checkbox"
                      checked={selectedFiles.has(file.file_path)}
                      onChange={() => toggleFile(file.file_path)}
                      className="rounded border-[#CBD5E1]"
                    />
                  )}
                </td>
                <td className="py-2 pr-3">
                  <div className="font-mono text-[#0F172A] text-xs break-all">
                    {file.file_name}
                  </div>
                  <div className="text-[#94A3B8] text-xs mt-0.5 truncate max-w-xs" title={file.file_path}>
                    {file.file_path}
                  </div>
                </td>
                <td className="py-2 pr-3">
                  {file.blueprint_type ? (
                    <span
                      className="text-xs font-bold uppercase tracking-wide"
                      style={{ color: typeColors[file.blueprint_type] || "#64748B" }}
                    >
                      {typeLabels[file.blueprint_type] || file.blueprint_type}
                    </span>
                  ) : (
                    <span className="text-xs text-[#94A3B8]">--</span>
                  )}
                </td>
                <td className="py-2 pr-3 font-mono text-xs text-[#0F172A]">
                  {file.machine_number || "--"}
                </td>
                <td className="py-2 pr-3 text-xs text-[#64748B] font-mono">
                  {formatSize(file.size)}
                </td>
                <td className="py-2">
                  {file.imported ? (
                    <span className="flex items-center gap-1 text-[#10B981] text-xs font-bold">
                      <CheckCircle2 size={12} /> Imported
                    </span>
                  ) : file.import_error ? (
                    <span className="text-[#DC2626] text-xs" title={file.import_error}>
                      Error
                    </span>
                  ) : (
                    <span className="text-[#94A3B8] text-xs">Pending</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ScanCard({ scan, onDelete, onPoll, onImportFiles }) {
  const isRunning = scan.status === "running";

  const statusConfig = {
    completed: { color: "#10B981", label: "COMPLETED", icon: CheckCircle2 },
    failed: { color: "#DC2626", label: "FAILED", icon: AlertTriangle },
    running: { color: "#2563EB", label: "SCANNING...", icon: Loader2 },
  };
  const st = statusConfig[scan.status] || statusConfig.running;
  const StatusIcon = st.icon;

  return (
    <div
      className="border border-[#E2E8F0] bg-white mb-4"
      data-testid={`scan-card-${scan.id}`}
    >
      <div className="p-4 border-b border-[#F1F5F9]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <HardDrive size={18} className="text-[#64748B]" />
            <span className="font-mono text-sm text-[#0F172A] font-bold break-all">
              {scan.scan_path}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span
              className="flex items-center gap-1 text-xs font-bold uppercase tracking-wide"
              style={{ color: st.color }}
              data-testid={`scan-status-${scan.id}`}
            >
              <StatusIcon size={14} className={isRunning ? "animate-spin" : ""} />
              {st.label}
            </span>
            {isRunning && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onPoll(scan.id)}
                className="text-[#64748B] hover:text-[#0F172A]"
                data-testid={`refresh-scan-${scan.id}`}
              >
                <RefreshCw size={14} />
              </Button>
            )}
            {!isRunning && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDelete(scan.id)}
                className="text-[#94A3B8] hover:text-[#DC2626]"
                data-testid={`delete-scan-${scan.id}`}
              >
                <Trash2 size={14} />
              </Button>
            )}
          </div>
        </div>

        <div className="flex gap-6 mt-3 text-xs text-[#64748B] font-mono">
          <span>Found: <strong className="text-[#0F172A]">{scan.files_found}</strong></span>
          <span>Imported: <strong className="text-[#10B981]">{scan.files_imported}</strong></span>
          {scan.files_skipped > 0 && (
            <span>Pending: <strong className="text-[#EAB308]">{scan.files_skipped}</strong></span>
          )}
          {scan.files_errors > 0 && (
            <span>Errors: <strong className="text-[#DC2626]">{scan.files_errors}</strong></span>
          )}
          <span>Scanned: {new Date(scan.created_at).toLocaleString()}</span>
        </div>
      </div>

      {scan.error && (
        <div className="px-4 py-2 bg-[#FEF2F2] border-b border-[#FECACA] text-[#DC2626] text-xs">
          <AlertTriangle size={12} className="inline mr-1" />
          {scan.error}
        </div>
      )}

      {scan.warnings && scan.warnings.length > 0 && (
        <div className="px-4 py-2 bg-[#FFFBEB] border-b border-[#FDE68A] text-xs text-[#92400E]">
          {scan.warnings.map((w, i) => (
            <div key={i} className="flex items-start gap-1">
              <AlertTriangle size={10} className="mt-0.5 shrink-0" />
              <span>{w}</span>
            </div>
          ))}
        </div>
      )}

      {scan.results && scan.results.length > 0 && (
        <div className="p-4">
          <ScanResultsTable
            results={scan.results}
            onImportFiles={onImportFiles}
            scanId={scan.id}
            scanStatus={scan.status}
          />
        </div>
      )}
    </div>
  );
}

export default function NetworkScanPage({ onLogout, userRole, isFeatureEnabled }) {
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [scanPath, setScanPath] = useState("");
  const [autoImport, setAutoImport] = useState(false);
  const [importing, setImporting] = useState(false);
  const pollTimerRef = useRef(null);

  // Scheduled paths state
  const [scheduledPaths, setScheduledPaths] = useState([]);
  const [scheduledStatus, setScheduledStatus] = useState([]);
  const [newScheduledPath, setNewScheduledPath] = useState("");
  const [newScheduledLabel, setNewScheduledLabel] = useState("");
  const [runningScheduled, setRunningScheduled] = useState(false);

  const fetchScans = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/scan`, { headers: getAuthHeaders() });
      setScans(response.data);
    } catch (error) {
      toast.error("Failed to load scan history");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchScheduledPaths = useCallback(async () => {
    try {
      const [pathsRes, statusRes] = await Promise.all([
        axios.get(`${API}/scheduled-paths`, { headers: getAuthHeaders() }),
        axios.get(`${API}/scheduled-paths/status`, { headers: getAuthHeaders() }),
      ]);
      setScheduledPaths(pathsRes.data);
      setScheduledStatus(statusRes.data);
    } catch {}
  }, []);

  useEffect(() => {
    fetchScans();
    fetchScheduledPaths();
    return () => { if (pollTimerRef.current) clearInterval(pollTimerRef.current); };
  }, [fetchScans, fetchScheduledPaths]);

  // Poll running scans
  useEffect(() => {
    const hasRunning = scans.some((s) => s.status === "running");
    if (hasRunning && !pollTimerRef.current) {
      pollTimerRef.current = setInterval(fetchScans, 3000);
    } else if (!hasRunning && pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, [scans, fetchScans]);

  const handleStartScan = async (e) => {
    e.preventDefault();
    if (!scanPath.trim()) return;

    setScanning(true);
    try {
      const resp = await axios.post(
        `${API}/scan/start`,
        { scan_path: scanPath.trim(), auto_import: autoImport },
        { headers: getAuthHeaders() }
      );
      toast.success(`Scan started for: ${scanPath}`);
      setScanPath("");
      // Immediately refresh scans to show the running one
      fetchScans();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start scan");
    } finally {
      setScanning(false);
    }
  };

  const handlePollScan = async (scanId) => {
    try {
      const resp = await axios.get(`${API}/scan/${scanId}`, {
        headers: getAuthHeaders(),
      });
      setScans((prev) => prev.map((s) => (s.id === scanId ? resp.data : s)));
    } catch (error) {
      toast.error("Failed to refresh scan status");
    }
  };

  const handleDeleteScan = async (scanId) => {
    try {
      await axios.delete(`${API}/scan/${scanId}`, { headers: getAuthHeaders() });
      setScans((prev) => prev.filter((s) => s.id !== scanId));
      toast.success("Scan record deleted");
    } catch (error) {
      toast.error("Failed to delete scan");
    }
  };

  const handleImportFiles = async (scanId, filePaths) => {
    setImporting(true);
    try {
      const resp = await axios.post(`${API}/scan/${scanId}/import`, filePaths, { headers: getAuthHeaders() });
      const { imported_count, error_count } = resp.data;
      if (imported_count > 0) toast.success(`Imported ${imported_count} file(s) as blueprints`);
      if (error_count > 0) toast.error(`${error_count} file(s) failed to import`);
      handlePollScan(scanId);
    } catch { toast.error("Import failed"); }
    finally { setImporting(false); }
  };

  const handleAddScheduledPath = async (e) => {
    e.preventDefault();
    if (!newScheduledPath.trim()) return;
    try {
      await axios.post(`${API}/scheduled-paths`, { path: newScheduledPath.trim(), label: newScheduledLabel.trim() }, { headers: getAuthHeaders() });
      toast.success("Scheduled path added");
      setNewScheduledPath("");
      setNewScheduledLabel("");
      fetchScheduledPaths();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add path");
    }
  };

  const handleTogglePath = async (pathId) => {
    try {
      const resp = await axios.put(`${API}/scheduled-paths/${pathId}/toggle`, {}, { headers: getAuthHeaders() });
      toast.success(`Path ${resp.data.enabled ? "enabled" : "disabled"}`);
      fetchScheduledPaths();
    } catch { toast.error("Failed to toggle path"); }
  };

  const handleDeletePath = async (pathId) => {
    try {
      await axios.delete(`${API}/scheduled-paths/${pathId}`, { headers: getAuthHeaders() });
      toast.success("Scheduled path removed");
      fetchScheduledPaths();
    } catch { toast.error("Failed to delete path"); }
  };

  const handleRunNow = async () => {
    setRunningScheduled(true);
    try {
      const resp = await axios.post(`${API}/scheduled-paths/run-now`, {}, { headers: getAuthHeaders() });
      toast.success(resp.data.message);
      fetchScans();
      fetchScheduledPaths();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to run scans");
    } finally { setRunningScheduled(false); }
  };

  if (loading) {
    return (
      <Layout onLogout={onLogout} userRole={userRole}>
        <div className="flex items-center justify-center h-64">
          <div className="text-[#64748B] font-mono">Loading scan history...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout onLogout={onLogout} userRole={userRole}>
      <div className="space-y-6" data-testid="network-scan-page">
        <div>
          <h1
            className="font-heading font-black text-4xl text-[#0F172A] mb-2"
            data-testid="network-scan-title"
          >
            Network Scanner
          </h1>
          <p className="text-[#64748B] text-sm">
            Scan Windows network paths, UNC shares, and local directories for blueprints
          </p>
        </div>

        {/* Scan Form */}
        <div className="bg-white border border-[#E2E8F0] p-6">
          <h2 className="font-heading font-bold text-lg text-[#0F172A] mb-4">
            Start New Scan
          </h2>
          <form onSubmit={handleStartScan}>
            <div className="mb-4">
              <Label className="text-[#0F172A] mb-2 block">
                Network Path or Directory
              </Label>
              <Input
                value={scanPath}
                onChange={(e) => setScanPath(e.target.value)}
                placeholder="\\server\blueprints  or  C:\Documents\Blueprints"
                required
                className="rounded-none border-[#E2E8F0] font-mono"
                data-testid="scan-path-input"
              />
              <p className="text-[#94A3B8] text-xs mt-1.5">
                Supports UNC paths (\\server\share), mapped drives (Z:\Prints), and local paths (C:\Blueprints)
              </p>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-[#64748B] cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoImport}
                  onChange={(e) => setAutoImport(e.target.checked)}
                  className="rounded border-[#CBD5E1]"
                  data-testid="auto-import-checkbox"
                />
                Auto-import found files into blueprint system
              </label>

              <Button
                type="submit"
                disabled={scanning || !scanPath.trim()}
                className="bg-[#2563EB] hover:bg-[#0F172A] text-white rounded-none transition-colors duration-150 tracking-wide"
                data-testid="start-scan-button"
              >
                {scanning ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    SCANNING...
                  </>
                ) : (
                  <>
                    <Search size={16} className="mr-2" />
                    START SCAN
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>

        {/* Supported file types */}
        <div className="bg-[#F8FAFC] border border-[#E2E8F0] p-4">
          <h3 className="text-xs font-bold text-[#64748B] uppercase tracking-wide mb-2">
            Recognized File Types
          </h3>
          <div className="flex flex-wrap gap-2">
            {[".pdf", ".dwg", ".dxf", ".tiff", ".tif", ".png", ".jpg", ".svg", ".step", ".stp", ".iges", ".doc", ".docx", ".xls", ".xlsx"].map((ext) => (
              <span key={ext} className="px-2 py-0.5 bg-white border border-[#E2E8F0] text-[#0F172A] text-xs font-mono">{ext}</span>
            ))}
          </div>
        </div>

        {/* Scheduled Scan Paths */}
        <div className="bg-white border border-[#E2E8F0]" data-testid="scheduled-paths-section">
          <div className="p-4 border-b border-[#E2E8F0] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock size={18} className="text-[#2563EB]" />
              <h2 className="font-heading font-bold text-lg text-[#0F172A]">Monthly Auto-Scan Paths</h2>
              <span className="text-xs text-[#64748B] font-mono ml-2">Runs 1st of every month</span>
            </div>
            <div className="flex items-center gap-2">
              {scheduledPaths.some((p) => p.enabled) && userRole === "admin" && (
                <Button
                  onClick={handleRunNow}
                  disabled={runningScheduled}
                  className="bg-[#10B981] hover:bg-[#059669] text-white rounded-none text-sm"
                  data-testid="run-scheduled-now-button"
                >
                  {runningScheduled ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Play size={14} className="mr-1" />}
                  Run Now
                </Button>
              )}
            </div>
          </div>

          {/* Add path form (admin only) */}
          {(userRole === "admin" || userRole === "superintendent") && (
            <form onSubmit={handleAddScheduledPath} className="p-4 border-b border-[#F1F5F9] flex gap-3 items-end">
              <div className="flex-1">
                <Label className="text-xs text-[#64748B]">Network Path</Label>
                <Input
                  value={newScheduledPath}
                  onChange={(e) => setNewScheduledPath(e.target.value)}
                  placeholder="\\server\blueprints or C:\Blueprints"
                  className="rounded-none border-[#E2E8F0] font-mono text-sm"
                  required
                  data-testid="scheduled-path-input"
                />
              </div>
              <div className="w-48">
                <Label className="text-xs text-[#64748B]">Label</Label>
                <Input
                  value={newScheduledLabel}
                  onChange={(e) => setNewScheduledLabel(e.target.value)}
                  placeholder="e.g. Main Share"
                  className="rounded-none border-[#E2E8F0] text-sm"
                  data-testid="scheduled-label-input"
                />
              </div>
              <Button type="submit" className="bg-[#2563EB] hover:bg-[#0F172A] text-white rounded-none text-sm" data-testid="add-scheduled-path-button">
                <Plus size={14} className="mr-1" /> Add
              </Button>
            </form>
          )}

          {/* Paths list */}
          {scheduledPaths.length === 0 ? (
            <div className="p-8 text-center">
              <Clock size={36} className="mx-auto text-[#CBD5E1] mb-3" />
              <p className="text-[#64748B] text-sm">No scheduled scan paths configured.</p>
              {(userRole === "admin" || userRole === "superintendent") && <p className="text-[#94A3B8] text-xs mt-1">Add paths above to enable monthly auto-scanning.</p>}
            </div>
          ) : (
            <div className="divide-y divide-[#F1F5F9]">
              {scheduledStatus.map((sp) => {
                const ls = sp.last_scan;
                return (
                  <div key={sp.path_id} className="p-4 flex items-center justify-between" data-testid={`scheduled-path-row-${sp.path_id}`}>
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <button
                        onClick={() => handleTogglePath(sp.path_id)}
                        className={`shrink-0 w-8 h-5 rounded-full transition-colors ${sp.enabled ? "bg-[#10B981]" : "bg-[#CBD5E1]"}`}
                        title={sp.enabled ? "Enabled — click to disable" : "Disabled — click to enable"}
                        data-testid={`toggle-path-${sp.path_id}`}
                      >
                        <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform ${sp.enabled ? "translate-x-3.5" : "translate-x-0.5"}`} />
                      </button>
                      <div className="min-w-0">
                        <div className="font-mono text-sm text-[#0F172A] truncate">{sp.path}</div>
                        <div className="flex items-center gap-3 text-xs text-[#64748B] mt-0.5">
                          {sp.label && <span>{sp.label}</span>}
                          {ls ? (
                            <>
                              <span>Last: {ls.status === "completed" ? `${ls.files_found} files found` : ls.status}</span>
                              {ls.completed_at && <span>{new Date(ls.completed_at).toLocaleDateString()}</span>}
                            </>
                          ) : (
                            <span>Never scanned</span>
                          )}
                        </div>
                      </div>
                    </div>
                    {(userRole === "admin" || userRole === "superintendent") && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeletePath(sp.path_id)}
                        className="text-[#94A3B8] hover:text-[#DC2626] shrink-0"
                        data-testid={`delete-scheduled-${sp.path_id}`}
                      >
                        <Trash2 size={14} />
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Scan History */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2
              className="font-heading font-bold text-lg text-[#0F172A]"
              data-testid="scan-history-title"
            >
              Scan History
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchScans}
              className="text-[#64748B] hover:text-[#0F172A]"
              data-testid="refresh-scans-button"
            >
              <RefreshCw size={14} className="mr-1" />
              Refresh
            </Button>
          </div>

          {scans.length === 0 ? (
            <div className="bg-white border border-[#E2E8F0] text-center py-12">
              <FolderOpen size={48} className="mx-auto text-[#CBD5E1] mb-4" />
              <p className="text-[#64748B]">No scans performed yet.</p>
              <p className="text-[#94A3B8] text-sm mt-1">
                Enter a path above and click Start Scan to begin.
              </p>
            </div>
          ) : (
            scans.map((scan) => (
              <ScanCard
                key={scan.id}
                scan={scan}
                onDelete={handleDeleteScan}
                onPoll={handlePollScan}
                onImportFiles={handleImportFiles}
              />
            ))
          )}
        </div>

        {/* Info box */}
        <div className="bg-[#F0F9FF] border border-[#BAE6FD] p-4">
          <h3 className="font-bold text-sm text-[#0369A1] mb-1">
            Windows Network Configuration
          </h3>
          <p className="text-[#0C4A6E] text-sm leading-relaxed">
            For scanning network shares, ensure the server running Vault has read access
            to the target UNC paths. Map network drives or configure share permissions as needed.
            The scanner automatically detects blueprint types (mechanical, electrical, hydraulic)
            and extracts machine numbers from filenames.
          </p>
        </div>
      </div>

      {importing && (
        <div className="fixed inset-0 bg-[#0F172A]/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 border border-[#E2E8F0] flex items-center gap-3">
            <Loader2 size={20} className="animate-spin text-[#2563EB]" />
            <span className="text-[#0F172A] font-medium">Importing files...</span>
          </div>
        </div>
      )}
    </Layout>
  );
}
